import React, { FC, useEffect, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Icon } from '@kontentino/ui';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faShareNodes } from '@fortawesome/pro-regular-svg-icons/faShareNodes';
import Modal from 'components/shared/Modal';
import ModalFooter from 'components/shared/modal/ModalFooter';
import ProfileSelect from './ProfileSelect';
import InviteLinkShare from './InviteLinkShare';
import { useToast } from 'app/hooks/useToast';
import { ApiClientError } from 'api/client';
import UserApi from 'api/user';
import { usePage } from 'modules/page/pageSelector';
import { PageData, ProfileGroup } from 'types/Page';
import { MODULE_STATUS } from 'constants/modules';
import { InviteUserResponse } from 'types/User';
import { userRoles } from './userRoles';
import Logger from 'utils/logger';

interface InviteUserSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  inviteResponse: InviteUserResponse;
  initialProfileIds?: number[];
}

const visibleProfileIds = (ids: number[], pages: PageData[]): number[] =>
  ids.filter((id) => pages.some((page) => page.id === id));

const InviteUserSuccessModal: FC<InviteUserSuccessModalProps> = ({
  isOpen,
  onClose,
  inviteResponse,
  initialProfileIds = [],
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { pages, profileGroups, status } = usePage();

  const [selectedProfiles, setSelectedProfiles] = useState<number[]>(() =>
    visibleProfileIds(initialProfileIds, pages),
  );
  const hasAppliedInitialProfilesRef = useRef(selectedProfiles.length > 0);
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [isProfilesMenuOpen, setIsProfilesMenuOpen] = useState(false);
  const [isGroupsMenuOpen, setIsGroupsMenuOpen] = useState(false);

  useEffect(() => {
    if (hasAppliedInitialProfilesRef.current) return;

    const initialProfiles = visibleProfileIds(initialProfileIds, pages);

    if (initialProfiles.length === 0) return;

    hasAppliedInitialProfilesRef.current = true;
    setSelectedProfiles(initialProfiles);
  }, [initialProfileIds, pages]);

  const handleProfilesChange = (profileIds: number[]) => {
    hasAppliedInitialProfilesRef.current = true;
    setSelectedProfiles(profileIds);
  };

  const { user, invitation } = inviteResponse;
  const isDeliveryFailed = invitation.delivery_status === 'failed';

  const profileOptions = pages.map((profile: PageData) => ({
    label: profile.name,
    value: profile.id,
    page: profile,
  }));

  const groupOptions = profileGroups
    .filter(
      (group: ProfileGroup) => group.isEditable && group.sharedBy === null,
    )
    .map((group: ProfileGroup) => ({
      label: group.name,
      value: group.id,
      page: {
        ...group,
        type: 0,
        isDemo: false,
        isGlobal: false,
        isAutoschedule: false,
      } as PageData,
    }));

  const assignProfilesMutation = useMutation(
    (params: { userId: number; pageIds: string[] }) =>
      UserApi.assignProfilesToUser(params),
    {
      onError: (error: ApiClientError) => {
        toast(t('inviteUserModal.failedToAssignProfiles'), 'error');
        Logger.error(error);
      },
    },
  );

  const assignGroupsMutation = useMutation(
    (params: { userId: number; profileGroupIds: string[] }) =>
      UserApi.assignProfileGroupsToUser(params),
    {
      onError: (error: ApiClientError) => {
        toast(t('inviteUserModal.failedToAssignProfileGroups'), 'error');
        Logger.error(error);
      },
    },
  );

  const handleAssignProfiles = async () => {
    const promises: Promise<unknown>[] = [];

    if (selectedProfiles.length > 0) {
      promises.push(
        assignProfilesMutation.mutateAsync({
          userId: user.id,
          pageIds: selectedProfiles.map(String),
        }),
      );
    }

    if (selectedGroups.length > 0) {
      promises.push(
        assignGroupsMutation.mutateAsync({
          userId: user.id,
          profileGroupIds: selectedGroups.map(String),
        }),
      );
    }

    await Promise.all(promises);

    if (promises.length > 0) {
      toast(t('inviteUserModal.profilesAssignedSuccessfully'), 'success');
    }
    onClose();
  };

  const isAssigning =
    assignProfilesMutation.isLoading || assignGroupsMutation.isLoading;
  const hasSelections =
    selectedProfiles.length > 0 || selectedGroups.length > 0;

  return (
    <Modal open={isOpen} onClose={onClose} size="sm">
      <Modal.Header
        title={t('inviteUserModal.userInvitedTitle')}
        className="tw-pb-0"
      />
      <Modal.Content className="tw-py-6">
        {isDeliveryFailed ? (
          <Alert
            variant="warning"
            title={t('inviteUserModal.deliveryFailedTitle', {
              name: user.name,
            })}
            className="tw-mb-6"
            data-cy="users-management_invite-delivery-failed"
          >
            {t('inviteUserModal.deliveryFailedDescription')}
          </Alert>
        ) : (
          <div className="tw-mb-6 tw-rounded-lg tw-border tw-border-success-30 tw-bg-success-10 tw-p-4">
            <div className="tw-flex tw-items-start tw-gap-3">
              <Icon icon={faCheck} className="tw-mt-1 tw-text-success-100" />
              <div>
                <p className="tw-font-medium">
                  {t('inviteUserModal.invitationSuccessfullySent')}
                </p>
                <p className="tw-mt-1">
                  <strong>{user.name}</strong>{' '}
                  {t('inviteUserModal.invitationSentMessage')}{' '}
                  <strong>
                    {userRoles[user.role as keyof typeof userRoles]}
                  </strong>
                </p>
                <p className="tw-mt-2 tw-text-sm tw-text-grayscale-100">
                  {t('inviteUserModal.invitedNextSteps')}
                </p>
              </div>
            </div>
          </div>
        )}

        {invitation.invitation_link && (
          <InviteLinkShare
            link={invitation.invitation_link}
            inviteeName={user.name}
            expiresAt={invitation.link_expires_at ?? null}
          />
        )}

        <div className="tw-mb-4 tw-flex tw-items-center tw-gap-2">
          <Icon icon={faShareNodes} className="tw-text-grayscale-100" />
          <span className="tw-font-medium tw-text-grayscale-180">
            {t('inviteUserModal.assignProfilesHeadstart')}
          </span>
        </div>

        <p className="tw-mb-4 tw-text-sm tw-text-grayscale-100">
          {t('inviteUserModal.assignProfilesDescription')}
        </p>

        <div className="tw-flex tw-flex-col tw-gap-4">
          <ProfileSelect
            label={t('inviteUserForm.assignProfiles')}
            options={profileOptions}
            value={selectedProfiles}
            onChange={handleProfilesChange}
            isDisabled={isAssigning || status === MODULE_STATUS.Loading}
            isMenuOpen={isProfilesMenuOpen}
            onMenuOpen={() => setIsProfilesMenuOpen(true)}
            onMenuClose={() => setIsProfilesMenuOpen(false)}
            noOptionsMessage={t('inviteUserForm.noProfilesAvailable')}
            valueContainerLabel="inviteUserForm.profilesSelected"
          />

          <ProfileSelect
            label={t('inviteUserForm.assignProfileGroups')}
            options={groupOptions}
            value={selectedGroups}
            onChange={setSelectedGroups}
            isDisabled={isAssigning || status === MODULE_STATUS.Loading}
            isMenuOpen={isGroupsMenuOpen}
            onMenuOpen={() => setIsGroupsMenuOpen(true)}
            onMenuClose={() => setIsGroupsMenuOpen(false)}
            noOptionsMessage={t('inviteUserForm.noProfileGroupsAvailable')}
            valueContainerLabel="inviteUserForm.profileGroupsSelected"
          />
        </div>
      </Modal.Content>
      <ModalFooter withDivider className="tw-flex tw-justify-end tw-gap-2">
        <Button variant="secondary" onClick={onClose} disabled={isAssigning}>
          {t('inviteUserModal.skipForNow')}
        </Button>
        <Button
          onClick={handleAssignProfiles}
          isLoading={isAssigning}
          disabled={!hasSelections}
        >
          {t('inviteUserModal.assignAndFinish')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default InviteUserSuccessModal;
